// Pure derivation of synthesised JTBD edges.
//
// The raw dialog graph already carries the JTBD relationships: a challenge
// `impedes` a job, an opportunity `improves` a job (written at dialog submit).
// When same-meaning raw items are promoted into synthesised nodes, we want those
// relationships to read at the synthesised level too — without a second AI call.
// We "lift" each raw spine edge through cluster membership: replace an endpoint
// with the synthesised node(s) that grouped it (an item can belong to more than
// one), or keep it as-is if it was never clustered. A derived edge is emitted
// only when at least one endpoint is a synthesised node — otherwise it's just the
// original raw edge, which stands on its own.
//
// Computed at render time, never persisted: promotion order doesn't matter (a
// challenge-theme can be promoted before its job-theme exists), and there are no
// stale edges to clean up when a node is dismissed or re-promoted.

// The raw edge types we lift. Everything else is out of scope: `evidences` is
// provenance (must not lift), and `mitigates`/`enables`/`depends-on`/`blocks`
// involve capabilities, which aren't synthesised today.
const LIFTABLE_EDGE_TYPES = new Set(['impedes', 'improves']);

export interface SynthEdgeNodeInput {
  id: string;
  // metadata.source — 'synthesis' marks a promoted synthesised node.
  source?: string;
  // synthesised nodes only: the raw dialog item ids this node groups.
  memberItemIds?: string[];
}

export interface SynthEdgeInput {
  source: string;
  target: string;
  edgeType: string;
}

export interface DerivedEdge {
  id: string;
  source: string;
  target: string;
  edgeType: string;
}

export interface ClusterNodeInput {
  id: string;
  source?: string;
  memberItemIds?: string[];
}

// Raw dialog item ids absorbed into a synthesised node (union of every synth
// node's memberItemIds). The synthesised primary view hides these — their
// synth node represents them. Shared by the full canvas and the peek dot map.
export function deriveClusteredRawIds(nodes: ClusterNodeInput[]): Set<string> {
  const ids = new Set<string>();
  for (const n of nodes) {
    if (n.source !== 'synthesis' || !Array.isArray(n.memberItemIds)) continue;
    for (const id of n.memberItemIds) ids.add(id);
  }
  return ids;
}

export function deriveSynthesisEdges(nodes: SynthEdgeNodeInput[], edges: SynthEdgeInput[]): DerivedEdge[] {
  // rawItemId -> synthesised node ids that grouped it (multi-membership: a list).
  const liftMap = new Map<string, string[]>();
  const synthIds = new Set<string>();
  for (const n of nodes) {
    if (n.source !== 'synthesis' || !Array.isArray(n.memberItemIds)) continue;
    synthIds.add(n.id);
    for (const itemId of n.memberItemIds) {
      const arr = liftMap.get(itemId);
      if (arr) arr.push(n.id);
      else liftMap.set(itemId, [n.id]);
    }
  }
  if (synthIds.size === 0) return []; // nothing promoted → nothing to derive

  // The synthesised node(s) that grouped this item, or the item itself (kept raw)
  // if it was never clustered.
  const lift = (id: string): string[] => liftMap.get(id) ?? [id];

  const seen = new Set<string>();
  const out: DerivedEdge[] = [];
  for (const e of edges) {
    if (!LIFTABLE_EDGE_TYPES.has(e.edgeType)) continue;
    for (const s of lift(e.source)) {
      for (const t of lift(e.target)) {
        if (s === t) continue; // no self-loops (homogeneity makes this rare, but guard anyway)
        if (!synthIds.has(s) && !synthIds.has(t)) continue; // both still raw → original edge stands
        const id = `derived:${e.edgeType}:${s}:${t}`;
        if (seen.has(id)) continue; // many raw edges can lift to the same synthesised pair
        seen.add(id);
        out.push({ id, source: s, target: t, edgeType: e.edgeType });
      }
    }
  }
  return out;
}
